// src/pages/Home/Pricing.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBolt,
  FaCheck,
  FaCrown,
  FaInfoCircle,
  FaRocket,
  FaShieldAlt,
  FaTimes,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";

const plans = [
  {
    key: "demo",
    title: "Mode Démo",
    shortTitle: "Démo",
    price: "Gratuit",
    priceNote: "Découverte",
    description:
      "Découvrez le fonctionnement général de MonAppliTournoi avec des données fictives.",
    features: [
      { text: "Accès aux principales fonctionnalités", included: true },
      { text: "Données fictives pour tester", included: true },
      { text: "Compétition réelle active", included: false },
      { text: "Résultats publics réels", included: false },
    ],
    icon: FaBolt,
    accent: "from-sky-500 to-indigo-500",
    isPopular: false,
  },
  {
    key: "unit",
    title: "Achat à l’unité",
    shortTitle: "TournoiPro",
    price: "25€",
    priceNote: "par compétition",
    description:
      "Idéal pour un club qui organise une ou deux compétitions dans l’année.",
    features: [
      { text: "Toutes les fonctionnalités activées", included: true },
      { text: "Accès à une compétition", included: true },
      { text: "Validité de la compétition : 3 mois", included: true },
      { text: "QR Code & résultats publics", included: true },
      { text: "Affichage live des résultats", included: true },
      { text: "Double affichage du tableau de score", included: true },
    ],
    icon: FaTrophy,
    accent: "from-emerald-500 to-teal-500",
    isPopular: false,
  },
  {
    key: "annual",
    title: "Formule Annuelle",
    shortTitle: "TournoiUltim",
    price: "50€",
    priceNote: "par an",
    description:
      "La formule la plus avantageuse pour gérer plusieurs compétitions toute l’année.",
    features: [
      { text: "Toutes les fonctionnalités activées", included: true },
      { text: "Compétitions illimitées", included: true },
      { text: "Validité de la formule : 1 an", included: true },
      { text: "QR Code & résultats publics", included: true },
      { text: "Affichage live des résultats", included: true },
      { text: "Double affichage du tableau de score", included: true },
    ],
    icon: FaCrown,
    accent: "from-indigo-500 via-violet-500 to-pink-500",
    isPopular: true,
  },
];

const trustItems = [
  {
    icon: FaShieldAlt,
    title: "Paiement sécurisé",
    text: "La souscription se fait sur une plateforme sécurisée. Envoi de facture automatisée.",
  },
  {
    icon: FaUsers,
    title: "Pensé pour les clubs",
    text: "Une solution adaptée aux tournois interclubs, animations et compétitions.",
  },
  {
    icon: FaRocket,
    title: "Mise en route rapide",
    text: "Possibilité de visio pour une démonstration rapide.",
  },
];

const useInView = (options = { threshold: 0.12 }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, options);

    observer.observe(element);

    return () => observer.disconnect();
  }, [options]);

  return [ref, inView];
};

const PricingFeature = ({ feature }) => {
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
          feature.included
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
            : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
        }`}
      >
        {feature.included ? <FaCheck /> : <FaTimes />}
      </span>

      <span
        className={`text-sm font-medium leading-relaxed ${
          feature.included
            ? "text-slate-700 dark:text-slate-200"
            : "text-slate-400 line-through decoration-1 dark:text-slate-500"
        }`}
      >
        {feature.text}
      </span>
    </li>
  );
};

const PricingCard = ({ plan }) => {
  const Icon = plan.icon;

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-[2rem] border bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:bg-slate-900 ${
        plan.isPopular
          ? "border-violet-300 ring-4 ring-violet-100 dark:border-violet-500/40 dark:ring-violet-900/40"
          : "border-slate-200 dark:border-white/10"
      }`}
    >
      <div
        className={`absolute inset-x-0 top-0 h-2 bg-gradient-to-r ${plan.accent}`}
      />

      {plan.isPopular && (
        <div className="absolute right-5 top-5 z-10 rounded-full bg-gradient-to-r from-indigo-600 to-pink-500 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg">
          Le plus avantageux
        </div>
      )}

      <div className="pt-7">
        <div
          className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${plan.accent} text-xl text-white shadow-lg transition group-hover:scale-105`}
        >
          <Icon />
        </div>

        <h3 className="text-2xl font-bold text-slate-950 dark:text-white">
          {plan.title}
        </h3>

        <p className="mt-2 min-h-[52px] text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          {plan.description}
        </p>

        <div className="mt-6 rounded-[1.65rem] bg-slate-50 p-5 dark:bg-slate-950/60">
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
              {plan.price}
            </span>

            <span className="pb-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
              {plan.priceNote}
            </span>
          </div>

          <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            {plan.shortTitle}
          </div>
        </div>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {plan.features.map((feature, index) => (
          <PricingFeature key={index} feature={feature} />
        ))}
      </ul>
    </article>
  );
};

const TrustItem = ({ icon: Icon, title, text }) => {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300">
          <Icon />
        </div>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
};

const Pricing = () => {
  const [ref, inView] = useInView();

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

      <div
        ref={ref}
        className={`relative transition-all duration-700 ${
          inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {trustItems.map((item) => (
            <TrustItem key={item.title} {...item} />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-stretch">
          {plans.map((plan) => (
            <PricingCard key={plan.key} plan={plan} />
          ))}
        </div>

        <div className="mt-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
          <div className="relative p-5 sm:p-6">
            <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-orange-500/10 blur-3xl" />

            <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaInfoCircle />
                  Souscription
                </div>

                <h3 className="text-xl font-bold text-slate-950 dark:text-white sm:text-2xl">
                  Visitez notre page des formules.
                </h3>

                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                  Découvrez les détails de chaque formule, les fonctionnalités
                  incluses et les avantages pour votre club. Vous pourrez
                  choisir la formule qui correspond le mieux à vos besoins et à
                  votre budget.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:shrink-0">
                <Link
                  to="/formules-tarifs"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:bg-orange-600"
                >
                  Voir les formules détaillées
                  <FaArrowRight className="h-3.5 w-3.5" />
                </Link>

                <a
                  href="#contact"
                  className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
                >
                  Demander une démo
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
